import { Injectable } from '@angular/core';
import { catchError, defer, from, map, Observable, of, shareReplay, switchMap, throwError } from 'rxjs';
import { NotificationService } from './notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { MAX_CACHE_SIZE_MB } from '../enum/constants';

@Injectable({
  providedIn: 'root',
})
export class CacheService {
  /** Object URLs of loaded remote files by `${cache name}|${url}`, shared across the app for the whole session */
  private readonly objectUrls = new Map<string, Observable<string>>();

  constructor(
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService,
  ) {
  }

  getCachedOrFetchBlob(url: string, fetch$: Observable<Blob>, name: string): Observable<Blob | null> {
    return from(caches.open(name)).pipe(
      switchMap((cache) =>
        from(cache.match(url)).pipe(
          switchMap((cachedResponse) => {
            if (cachedResponse) {
              return from(cachedResponse.blob());
            } else {
              return fetch$.pipe(
                switchMap((blob) => {
                  const blobSizeMB = blob.size / (1024 * 1024);

                  if (blobSizeMB >= MAX_CACHE_SIZE_MB) {
                    return of(blob);
                  }

                  const responseToCache = new Response(blob);

                  return from(cache.put(url, responseToCache)).pipe(
                    switchMap(() => of(blob)),
                  );
                }),
              );
            }
          }),
        ),
      ),
      catchError((err) => throwError(() => err))
    );
  }

  /**
   * Loads a remote file once per session and keeps it in the Cache API `name` between sessions,
   * so the same URL shown in many places (e.g. avatars) is requested only once.
   * Emits a local object URL, or the source URL itself when the file can not be fetched/cached.
   */
  getCachedObjectUrl(url: string, name: string): Observable<string> {
    const key = `${name}|${url}`;
    const cached = this.objectUrls.get(key);
    if (cached) return cached;

    // Native fetch on purpose: HttpClient interceptors would attach the auth token to third-party hosts
    const fetch$ = defer(() => fetch(url)).pipe(
      switchMap((response) => {
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        return from(response.blob());
      }),
    );

    const url$ = this.getCachedOrFetchBlob(url, fetch$, name).pipe(
      map((blob) => (blob ? URL.createObjectURL(blob) : url)),
      // Not cacheable (CORS, network): remember the plain URL so it is not retried by every caller
      catchError(() => of(url)),
      shareReplay(1),
    );

    this.objectUrls.set(key, url$);
    return url$;
  }

  clearCache(name: string) {
    from(caches.delete(name)).subscribe({
      next: (success) => {
        if (success) {
          this.notificationService.showSuccess(
            this.translate.instant('notifications_texts.success.success'),
            this.translate.instant('notifications_texts.success.successfully_cleared'),
          );
        } else {
          this.notificationService.showWarning(
            this.translate.instant('notifications_texts.warn.warn'),
            this.translate.instant('notifications_texts.warn.no_cache_to_clear'),
          );
        }
      },
      error: () => {
        this.notificationService.showError(
          this.translate.instant('notifications_texts.error.error'),
          this.translate.instant('notifications_texts.error.something_went_wrong_1'),
        );
      },
    });
  }
}
